import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const AuthenticationAndLoginPortal = () => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [activeTab, setActiveTab] = useState('user');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Try to sign in first
      const { data, error } = await signIn(username, password);
      
      // If user doesn't exist (Invalid login credentials), try to create the account
      if (error && (error?.message?.includes('Invalid login credentials') || error?.message?.includes('Email not confirmed'))) {
        // Auto-signup for demo user
        const { data: signUpData, error: signUpError } = await signUp(username, password, {
          username: username
        });
        
        if (signUpError) {
          setLoading(false);
          setError(signUpError?.message || 'Failed to create account. Please try again.');
          return;
        }
        
        // After successful signup, try signing in again
        const { data: signInData, error: signInError } = await signIn(username, password);
        
        if (signInError) {
          setLoading(false);
          setError('Account created but sign-in failed. Please try logging in again.');
          return;
        }
        
        // Successful authentication after auto-signup
        if (signInData?.user) {
          setTimeout(() => {
            setLoading(false);
            navigate('/project-landing-dashboard');
          }, 800);
        }
        return;
      }
      
      if (error) {
        setLoading(false);
        setError(error?.message || 'Invalid credentials');
        return;
      }

      // Successful authentication - user now has authenticated session
      if (data?.user) {
        setTimeout(() => {
          setLoading(false);
          navigate('/project-landing-dashboard');
        }, 800);
      } else {
        setLoading(false);
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      setLoading(false);
      setError(err?.message || 'Login failed. Please try again.');
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setError('');
    setUsername('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex flex-col">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-amber-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Header - 15% */}
      <header className="relative z-10 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
              <Icon name="Factory" size={28} color="white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">ModularFlow</h1>
              <p className="text-sm text-muted-foreground">Production Workflow Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span>Secure Access</span>
          </div>
        </div>
      </header>

      {/* Main Authentication Card - 60% */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Glassmorphism Card */}
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl border border-white/20 overflow-hidden">
            {/* Tab Header */}
            <div className="flex border-b border-border/50">
              <button
                onClick={() => handleTabSwitch('user')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-all duration-250 ${
                  activeTab === 'user' ?'bg-primary text-white shadow-md' :'bg-transparent text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="User" size={18} />
                  <span>User Login</span>
                </div>
              </button>
              <button
                onClick={() => handleTabSwitch('admin')}
                className={`flex-1 py-4 px-6 text-center font-medium transition-all duration-250 ${
                  activeTab === 'admin' ?'bg-primary text-white shadow-md' :'bg-transparent text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Icon name="ShieldCheck" size={18} />
                  <span>Admin Login</span>
                </div>
              </button>
            </div>

            {/* Form Content */}
            <div className="p-8">
              <div className="mb-6 text-center">
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  {activeTab === 'user' ? 'Welcome Back' : 'Administrator Access'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {activeTab === 'user' ?'Sign in to manage your projects and workflows' :'Sign in with administrator credentials'}
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                {error && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                    <Icon name="AlertCircle" size={18} color="var(--color-error)" />
                    <p className="text-sm text-error flex-1">{error}</p>
                  </div>
                )}

                <Input
                  type="text"
                  label="Username"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e?.target?.value)}
                  required
                  autoFocus
                  disabled={loading}
                />

                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      disabled={loading}
                    />
                    <span className="text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => alert('Password recovery not yet implemented')}
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={loading}
                  disabled={loading || !username || !password}
                  iconName="LogIn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              {activeTab === 'user' && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800 font-medium mb-1">Demo Credentials:</p>
                  <p className="text-xs text-blue-700">Email: <span className="font-mono font-semibold">ebi@modularflow.com</span></p>
                  <p className="text-xs text-blue-700">Password: <span className="font-mono font-semibold">password123</span></p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Need help? <button className="text-primary hover:underline">Contact Support</button>
            </p>
          </div>
        </div>
      </main>

      {/* Footer - 25% */}
      <footer className="relative z-10 py-8 px-6 border-t border-border/30 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Lock" size={20} color="var(--color-success)" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Secure Authentication</h4>
                <p className="text-xs text-muted-foreground">Enterprise-grade security with session management</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Role-Based Access</h4>
                <p className="text-xs text-muted-foreground">Different permissions for users and administrators</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} color="var(--color-warning)" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Quick Access</h4>
                <p className="text-xs text-muted-foreground">Keyboard shortcuts and biometric support</p>
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-muted-foreground">
            <p>© 2026 ModularFlow. All rights reserved. | Version 2.1.0</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthenticationAndLoginPortal;